<script setup>
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Inplace from "primevue/inplace";
import IconField from "primevue/iconfield";
import ToggleSwitch from "primevue/toggleswitch";
import InputIcon from "primevue/inputicon";
import Select from "primevue/select";
import {router, usePage} from "@inertiajs/vue3";
import {computed, nextTick, ref, watch} from "vue";
import _, {debounce, isString, uniqBy} from "lodash";
import AccordionHeader from "primevue/accordionheader";
import ServicesEdit from "../ui-components/ServicesEdit.vue";
import LangFlag from 'vue-lang-code-flags';
import AccordionPanel from "primevue/accordionpanel";
import Accordion from "primevue/accordion";
import AccordionContent from "primevue/accordioncontent";
import Quill from "quill";
import {getFormType, getPrettyHTML} from "../../../../helpers.js";
import EditTimeSlotComponent from "../ui-components/EditTimeSlotComponent.vue";
import FormBuilder from "../ui-components/FormBuilder.vue";
import FormThemingInteractive from "../ui-components/FormThemingInteractive.vue";
import FeatureBlocker from "../ui-components/FeatureBlocker.vue";
import CrownIcon from "../ui-components/CrownIcon.vue";
import ChangesCustomer from "../ui-components/settings/ChangesCustomer.vue";
import OccupancySettings from "../ui-components/settings/OccupancySettings.vue";
import LocationsComponent from "../ui-components/LocationsComponent.vue";
import {driver} from "driver.js";
import DrawerButton from "../ui-components/buttons/DrawerButton.vue";
import CalendarBasedTimeSlot from "../ui-components/time-slot/CalendarBasedTimeSlot.vue";
import PlanCalendar from "../ui-components/time-slot/PlanCalendar.vue";
import RentalCard from "../../wizards/registrationForm/rental/subjects/RentalCard.vue";
import RentalItemModal from "../../wizards/registrationForm/rental/subjects/RentalItemModal.vue";
import TypesCloud from "../../wizards/registrationForm/rental/subjects/TypesCloud.vue";
import {useRentalItemTypes} from "../../wizards/helpers/useRentalItemTypes.js";
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    forms: Array,
    isExpanded: Boolean,
    form: Object,
    shown_driver: Number,
    valid_days: Object,
    title: String,
    templates: Array,
    services: Array,
    theme: Object,
    settings: Object,
    formBuilder: Array,
    rental_items: Array,
    locations: Array,
    workers: Array,
    expandSidebar: Boolean,
})

const mounted = ref(false);
const theme = ref({})
const settings = ref({})
const form = ref({})
const timeSlots = ref([])
const services = ref([])
const builder = ref([])
const templates = ref([])
const locations = ref([])
const rentalItems = ref({
    rentalItems: [],
    reservationType: ''
})

const isMultiDayRental = computed(() =>
    ['rental', 'accommodation'].includes(props.form.form_type?.name) &&
    rentalItems.value.reservationType === 'multi-day'
)

const bookingRulesDays = [
    { label: trans('common.dow_long.mon'), value: 'monday' },
    { label: trans('common.dow_long.tue'), value: 'tuesday' },
    { label: trans('common.dow_long.wed'), value: 'wednesday' },
    { label: trans('common.dow_long.thu'), value: 'thursday' },
    { label: trans('common.dow_long.fri'), value: 'friday' },
    { label: trans('common.dow_long.sat'), value: 'saturday' },
    { label: trans('common.dow_long.sun'), value: 'sunday' },
]
const bookingRulesTypes = [
    { label: trans('rental.blocking_start'), value: 'start' },
    { label: trans('rental.blocking_end'), value: 'end' },
    { label: trans('rental.blocking_intersect'), value: 'intersect' },
]

// Shared booking rules shown in the accordion — kept in sync with all rental items
const formBookingRules = ref([])

watch(() => rentalItems.value.rentalItems, (items) => {
    if (!items?.length) return
    const incoming = JSON.stringify(items[0]?.booking_rules ?? [])
    if (incoming !== JSON.stringify(formBookingRules.value)) {
        formBookingRules.value = JSON.parse(incoming)
    }
}, { immediate: true, deep: true })

watch(formBookingRules, (rules) => {
    rentalItems.value.rentalItems.forEach(item => {
        if (item && typeof item === 'object') {
            item.booking_rules = JSON.parse(JSON.stringify(rules))
        }
    })
}, { deep: true })

let quill = null;
let AlignStyle = Quill.import('attributors/style/align')
let BackgroundStyle = Quill.import('attributors/style/background')
let ColorStyle = Quill.import('attributors/style/color')
let DirectionStyle = Quill.import('attributors/style/direction')
let FontStyle = Quill.import('attributors/style/font')
let SizeStyle = Quill.import('attributors/style/size')

Quill.register(AlignStyle, true);
Quill.register(BackgroundStyle, true);
Quill.register(ColorStyle, true);
Quill.register(DirectionStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

const openPersonalizeDriver = () => {
    const driverObj = driver({
        nextBtnText: trans('common.next'),
        prevBtnText: trans('common.back'),
        doneBtnText: trans('common.done'),
        showProgress: true,
        steps: [
            {
                element: '#form-wrapper',
                popover: {
                    title: trans('forms.theme_title'),
                    description: trans('forms.theme_description'),
                }
            },
            {element: '#form-logo', popover: {title: trans('forms.theme_branding'), description: trans('forms.theme_branding_description')}},
            {
                element: '#form-background',
                popover: {
                    title: trans('forms.theme_background'),
                    description: trans('forms.theme_background_description'),
                }
            },
            {
                element: '#form-panel',
                popover: {
                    title: trans('forms.theme_panel'),
                    description: trans('forms.theme_panel_description'),
                }
            },
            {
                element: '#form-dark-mode',
                popover: {
                    title: trans('forms.theme_dark_mode'),
                    description: trans('forms.theme_dark_mode_description'),
                }
            },
        ]
    });

    driverObj.drive();
}


const openAvailabilityDriver = () => {
    const availabilityDriverObj = driver({
        nextBtnText: trans('common.next'),
        prevBtnText: trans('common.back'),
        doneBtnText: trans('common.done'),
        showProgress: true,
        steps: [
            {
                element: '#availability-wrapper',
                popover: {
                    title: trans('forms.driver_avail_title'),
                    description: trans('forms.driver_avail_desc'),
                }
            },
            {
                element: '#day-0',
                popover: {
                    title: trans('forms.driver_avail_slot_title'),
                    description: trans('forms.driver_avail_slot_desc'),
                }
            },
            {
                element: '#selector-0',
                popover: {
                    title: trans('forms.driver_avail_type_title'),
                    description: trans('forms.driver_avail_type_desc'),
                }
            },
            {
                element: '#new-plan',
                popover: {
                    title: trans('forms.driver_avail_plan_title'),
                    description: trans('forms.driver_avail_plan_desc'),
                }
            },
            {
                element: '#new-free',
                popover: {
                    title: trans('forms.driver_avail_vacation_title'),
                    description: trans('forms.driver_avail_vacation_desc'),
                }
            },
            {
                element: '#show-more',
                popover: {
                    title: trans('forms.driver_avail_more_title'),
                    description: trans('forms.driver_avail_more_desc'),
                }
            },
        ]
    });
    availabilityDriverObj.drive();
}



const localeOptions = [
    {
        value: 'cs',
        label: trans('forms.language_cs'),
    },
    {
        value: 'en',
        label: trans('forms.language_en'),
    }/*,
    {
        value: 'ko',
        label: trans('forms.language_ko'),
    }*/
]

watch(props, (pageprops) => {
    pageprops = JSON.parse(JSON.stringify(pageprops));
    if (pageprops.form) {
        theme.value = {
            color: pageprops.theme.color,
            borderless: pageprops.theme.borderless,
            transparent: false,
            darkMode: pageprops.theme.mode_lock === 'dark',
            darkModeLock: pageprops.theme.mode_lock !== 'auto',
            background_color: pageprops.theme.background_color,
            background_color_dark: pageprops.theme.background_color_dark,
            background_image_url: pageprops.theme.background_image_url,
            background_image_url_dark: pageprops.theme.background_image_url_dark,
            logo_url: pageprops.theme.logo_url,
            logo_url_dark: pageprops.theme.logo_url_dark,
            panel_color: pageprops.theme.panel_color,
            panel_color_dark: pageprops.theme.panel_color_dark,
            panel_transparency: pageprops.theme.panel_transparency,
            panel_transparency_dark: pageprops.theme.panel_transparency_dark,
            panel_backdrop_blur: pageprops.theme.panel_backdrop_blur,
            panel_backdrop_blur_dark: pageprops.theme.panel_backdrop_blur_dark,
            fontFamily: pageprops.theme.fontFamily,
            rows: pageprops.theme.rows,
            columns: pageprops.theme.columns,
            roundness: pageprops.theme.roundness,
        }
// <rental-subjects v-model="rentalItems"></rental-subjects>
        rentalItems.value.rentalItems = pageprops.rental_items.map((item) => {
            return {
                id: item.id,
                name: item.name,
                description: item.description,
                icon: item.type,
                type: item.type,
                unit: item.unit,
                min_unit_quantity: item.min_unit_quantity,
                payment_style: item.payment_style,
                unit_price: item.prices[0]?.unit_price,
                vat_percentage: item.prices[0]?.vat_percentage,
                max_pop: item.max_pop,
                max_adults: item.max_adults ?? null,
                max_children: item.max_children ?? null,
                max_pets: item.max_pets ?? 0,
                currency: item.prices[0]?.currency,
                attributes: item.attributes,
                discounts: item.discounts,
                calendar_id: item.external_calendar.calendar_api_key,
                additional_costs: item.additional_costs,
                occupancy_rules: item.pricing_rules.map(rule => {
                    return {
                        ...rule,
                        show_price_change: rule.show_price_change === 1
                    }
                }),
                pop_rules: item.pop_rules,
                booking_rules: item.booking_rules,
                timeSlots: item.valid_days,
                gallery: item.gallery
            }
        });

        rentalItems.value.reservationType = pageprops.form.reservation_type;

        rentalItems.value.calendarProvider = pageprops.form.provider.name;

        templates.value = pageprops.templates;

        locations.value = pageprops.locations.map(location => {
            return {
                ...location,
                address: JSON.parse(location.address),
            }
        });

        settings.value = {
            firstDayOfWeek: pageprops.settings.firstDayOfWeek,
            titlePosition: pageprops.settings.titlePosition,
            transition: pageprops.settings.transition,
            locale: pageprops.settings.locale,
            showWeekNumbers: pageprops.settings.showWeekNumbers,
            view: pageprops.settings.view,
        }

        form.value = {
            name: pageprops.form?.name,
            language: pageprops.form?.locale,
            show_banner: pageprops.form?.show_banner,
            description: pageprops.form?.description,
            description_code: pageprops.form?.description_code,
            reservation_change_settings: pageprops.form?.reservation_change_settings,
            reservation_cancel_settings: pageprops.form?.reservation_cancel_settings,
            reservation_change_auto_approve: pageprops.form?.reservation_change_auto_approve,
            reservation_cancel_auto_approve: pageprops.form?.reservation_cancel_auto_approve,
            calendar_occupancy_settings: pageprops.form?.calendar_occupancy_settings,
            roori_occupancy_settings: pageprops.form?.roori_occupancy_settings,
            notifications: {
                newClient: true,
                confirmClient: true,
                changeClient: true,
                cancelClient: true,
                reminderClient: false,
                retargetingClient: false,
            }
        }
        timeSlots.value = Object.values(JSON.parse(JSON.stringify(pageprops.valid_days))).map(element => {
            if (element.valid.from) {
                element.valid.from = new Date(element.valid.from);
                element.valid.to = new Date(element.valid.to);
            }
            return element;
        }).sort((a, b) => {
            return a.valid.from === null ? -1 : 1;
        });
        services.value = pageprops.services;

        mounted.value = true;

        builder.value = pageprops.formBuilder;
        setTimeout(() => {
            quill = new Quill('#text1', {
                theme: 'snow',
                placeholder: trans('forms.setting_description'),
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{'color': []}, {'background': []}],
                        [{'header': '1'}, {'header': '2'}, 'blockquote'],
                        [{'align': []}],
                        ['clean']
                    ]
                }
            });
            quill.setContents(JSON.parse(form.value.description_code))
            form.value.description = getPrettyHTML(quill.container.firstChild.innerHTML);
            quill.on('text-change', () => {
                form.value.description_code = JSON.stringify(quill.getContents());
                form.value.description = getPrettyHTML(quill.container.firstChild.innerHTML);
            });
        }, 400)
    }
}, {immediate: true});
const deleted = ref([]);
const send = (goBack = 'false') => {
    router.post(usePage().url, {
        theme: theme.value,
        settings: settings.value,
        form: form.value,
        locations: locations.value,
        templates: templates.value,
        timeSlots: timeSlots.value.map((timeSlot) => {
            if (timeSlot.valid.from) {
                let from = timeSlot.valid.from;
                let to = timeSlot.valid.to;

                if (isString(from)) {
                    from = new Date(Date.parse(from));
                    to = new Date(Date.parse(to));
                }

                timeSlot.valid.from = from.getFullYear() + '-' + (from.getMonth() + 1).toString().padStart(2, '0') + '-' + (from.getDate()).toString().padStart(2, '0') + 'T00:00:00.000Z';
                timeSlot.valid.to = to.getFullYear() + '-' + (to.getMonth() + 1).toString().padStart(2, '0') + '-' + (to.getDate()).toString().padStart(2, '0') + 'T00:00:00.000Z';
            }
            return timeSlot;
        }),
        rentalItems: rentalItems.value.rentalItems,
        services: services.value,
        formBuilder: builder.value,
        timeSlotDeleted: deleted.value,
        goBack: goBack
    })
}

const showDrawer = ref(false);


watch(() => showDrawer.value, (val) => {
    if (val && val === 'theming' && !props.shown_driver) {
        axios.post('/shown-driver');
        openPersonalizeDriver()
    }

    if (val && val === 'availability') {
        nextTick().then(() => {
            window.addEventListener('resize', computeSlotHeight);
            computeSlotHeight();
        })
    } else {
        window.removeEventListener('resize', computeSlotHeight);
    }
});


const selectedPlan = ref('default');

const computedSelected = computed(() => {
    if (selectedPlan.value === 'default') {
        return 0;
    }
    return selectedPlan.value;
})

const previousSelected = ref(0);

const animationName = computed(() => {
    if (previousSelected.value === 0 && computedSelected.value > 0) {
        return 'slide-left';
    } else if (previousSelected.value > 0 && computedSelected.value === 0) {
        return 'slide-right';
    } else if (previousSelected.value > 0 && computedSelected.value > 0) {
        return 'fade';
    }
    return 'fade';
});

const slotHeight = ref(45);

const computeSlotHeight = () => {
    const totalSlotCount = document.querySelectorAll('#day-0 .day-part-time-slot')?.length / 2;

    const dayPartHeaderHeight = document.getElementsByClassName('day-part-header').length ? document.getElementsByClassName('day-part-header')[0].clientHeight : 0;
    const planCalendarHeight = document.getElementById('plan-calendar')?.clientHeight;
    const selectorOfTypeHeight = document.getElementsByClassName('selector-of-type').length ? document.getElementsByClassName('selector-of-type')[0].clientHeight : 0;
    const availableHeight = window.innerHeight - 20;

    const paddings = 3;

    const viewPart = availableHeight - planCalendarHeight - dayPartHeaderHeight - selectorOfTypeHeight - paddings;

    const result = viewPart / totalSlotCount;

    slotHeight.value = Number.isFinite(result) ? result : 45;
}

const selectedWorker = ref(null);

watch([() => selectedPlan.value, () => selectedWorker.value], () => {
    doComputeSlotHeight();
})

const doComputeSlotHeight = debounce(() => {
    nextTick().then(computeSlotHeight());
    setTimeout(() => computeSlotHeight(), 200);
}, 300)

const currentItem = ref(false);
const selectedType = ref(false);

const newRentalItem = (type) => {
    const newId = Date.now().toString() + Math.random().toString(36).substring(2);
    const defaultType = useRentalItemTypes().rentalItemTypes.find(l_type => l_type.type === type);
    currentItem.value = {
        id: newId,
        name: '',
        description: '',
        icon: defaultType?.icon,
        type: '',
        calendar_id: null,
        unit: props.form.reservation_type === 'multi-day' ? 'day' : 'hour',
        min_unit_quantity: 1,
        payment_style: 'before',
        unit_price: 0.00,
        vat_percentage: 0,
        currency: 'CZK',
        max_pop: 2,
        max_adults: null,
        max_children: null,
        max_pets: 0,
        attributes: defaultType?.defaultAttributes ?? [],
        discounts: [],
        additional_costs: [],
        occupancy_rules: [],
        booking_rules: [],
        timeSlots: [],
        gallery: []
    };
}

watch(() => selectedType.value, () => {
    newRentalItem(selectedType.value);
})

const reminderOptions = [
    { id: 'day_before',       label: trans('forms.reminder_day_before') },
    { id: 'two_days_before',  label: trans('forms.reminder_two_days_before') },
    { id: 'three_days_before',label: trans('forms.reminder_three_days_before') },
    { id: 'week_before',      label: trans('forms.reminder_week_before') },
    { id: 'month_before',     label: trans('forms.reminder_month_before') },
];

const getNotificationLabel = (type) => {
    const map = {
        new_client:        trans('reservations.notify_new_client'),
        confirm_client:    trans('reservations.notify_confirm'),
        change_client:     trans('reservations.notify_change'),
        cancel_client:     trans('reservations.notify_cancel'),
        reminder_client:   trans('reservations.notify_reminder'),
        retargeting_client:trans('reservations.notify_retargeting'),
    };
    return map[type] ?? trans('reservations.notify_unknown');
};
</script>

<template>
    <Drawer :visible="mounted" position="right" :show-close-icon="false" class="main-drawer">
        <template v-if="mounted" #container>
            <div id="progress-container"></div>
            <div class="absolute top-4 right-4">
                <Button icon="pi pi-times" severity="secondary" rounded text size="large"
                        @click="router.visit('/app/forms', { preserveScroll: true, preserveState: false})"></Button>
            </div>
            <div class="flex flex-col h-full pt-8">
                <div class="border-b dark:border-neutral-700 p-6 ">
                    <div class="flex gap-4 items-center">
                    <span :class="getFormType(props.form.form_type?.name)?.icon"
                          class="text-2xl  dark:text-white" style="font-size: 1.25rem;"></span>
                        <Inplace unstyled>
                            <template #content="{ closeCallback }">
                                <IconField>
                                    <InputText size="large" v-model="form.name" fluid></InputText>
                                    <InputIcon class="pi pi-save cursor-pointer" @click="closeCallback"></InputIcon>
                                </IconField>
                            </template>
                            <template #display>
                                <p class="text-3xl font-semibold hover:text-roori-blue cursor-text">{{ form.name }}</p>
                            </template>
                        </Inplace>
                    </div>
                    <p class="ml-9 text-black/30 dark:text-white/80">
                        {{
                            getFormType(props.form.form_type?.name)?.label
                        }}
                    </p>
                </div>

                <div class="grow overflow-y-scroll">
                    <div
                        class="border-b p-4 flex gap-4 bg-neutral-50 dark:bg-neutral-800 dark:border-neutral-700 max-md:hidden">

                                <DrawerButton :title="$t('forms.drawer_theming_title')" icon="pi pi-palette" :active="showDrawer === 'theming'"
                                               @click="showDrawer = (showDrawer === 'theming' ? false : 'theming')"
                                               :description="$t('forms.drawer_theming_desc')"></DrawerButton>


                        <DrawerButton v-if="!['rental', 'accommodation'].includes(props.form.form_type?.name)" :title="$t('forms.panel_availability')"
                                      icon="pi pi-clock" :active="showDrawer === 'availability'"
                                      @click="showDrawer = (showDrawer === 'availability' ? false : 'availability')"
                                      :description="$t('forms.drawer_availability_desc')"></DrawerButton>
                        <DrawerButton v-else :title="props.form.form_type?.name === 'accommodation' ? $t('forms.drawer_accommodation_title') : $t('forms.drawer_rental_items_title')" :icon="props.form.form_type?.name === 'accommodation' ? 'pi pi-home' : 'pi pi-box'"
                                      :active="showDrawer === 'rentalItems'"
                                      @click="showDrawer = (showDrawer === 'rentalItems' ? false : 'rentalItems')"
                                      :description="$t('forms.drawer_rental_items_desc')"></DrawerButton>
                    </div>

                    <Accordion value="999" class="overflow-hidden" expand-icon="pi pi-chevron-down"
                               collapse-icon="pi pi-chevron-up">
                        <AccordionPanel value="0">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-info-circle text-roori-blue"></i>
                                        {{ $t('forms.panel_basic_info') }}
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_basic_info_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <div>
                                    <div id="text1" class="dark:border-neutral-700"></div>
                                    <div class="grid grid-cols-3 mt-4 gap-3">
                                        <div v-for="language in localeOptions" :key="language.value"
                                             @click="form.language = language.value"
                                             class="cursor-pointer bg-gray-50 dark:bg-gray-900 flex gap-2 p-3 items-center justify-center rounded-xl border dark:border-neutral-700 select-none shadow transition-all hover:shadow-md"
                                             :class="form.language === language.value ? 'ring-2 ring-offset-2 dark:ring-offset-0 dark:ring-blue-700':''"
                                        >
                                            <lang-flag :iso="language.value" :squared="false"
                                                       :title="language.label"></lang-flag>
                                            <p class="text-sm">
                                                {{ language.label }}
                                            </p>
                                        </div>
                                    </div>
                                    <feature-blocker inline version="Pro" name="roori_can_remove_branding">
                                        <div class="flex gap-5 items-center mt-5">
                                            <ToggleSwitch v-model="form.show_banner"></ToggleSwitch>
                                            <p class="text-sm">{{ $t('forms.show_banner_label') }}</p>
                                        </div>
                                    </feature-blocker>
                                </div>
                            </AccordionContent>
                        </AccordionPanel>

                        <AccordionPanel value="7">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-map-marker text-roori-blue"></i>
                                        {{ $t('forms.panel_locations') }}
                                        <CrownIcon class="-ml-3" compute name="roori_places"></CrownIcon>
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_locations_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <FeatureBlocker name="roori_places" version="Pro">
                                    <locations-component v-model="locations" :form="props.form"></locations-component>
                                </FeatureBlocker>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel value="1">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-sync text-roori-blue"></i>
                                        {{ $t('forms.panel_changes') }}
                                        <CrownIcon class="-ml-3" name="roori_customer_can_change"></CrownIcon>
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_changes_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <FeatureBlocker name="roori_customer_can_change" version="Pro">
                                    <div
                                        class="settings-wrapper p-4 rounded-lg shadow-md border bg-white dark:bg-gray-900 dark:border-neutral-700">
                                        <changes-customer v-model="form"></changes-customer>
                                    </div>
                                </FeatureBlocker>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel value="4">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-envelope text-roori-blue"></i>
                                        {{ $t('forms.panel_notifications') }}
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_notifications_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <div class="grid grid-cols-2 gap-3">
                                    <template v-for="(template, i) in templates"
                                              :key="template.id">
                                        <div class="border flex flex-col rounded-xl shadow-md overflow-hidden dark:border-neutral-700"
                                             :class="template.type === 'reminder_client' ? 'col-span-2' : ''">
                                            <div class="p-2.5 flex items-center gap-3 border-b dark:border-neutral-700">
                                                <span
                                                    class="bg-gray-100 dark:bg-neutral-700 p-2 py-0.5 rounded-lg"
                                                    :class="[
                                                        'pi text-lg',
                                                        {
                                                            'pi-asterisk text-sky-500 bg-sky-50': template.type === 'new_client',
                                                            'pi-thumbs-up text-green-500 bg-green-50': template.type === 'confirm_client',
                                                            'pi-calendar text-amber-600 bg-amber-100/80': template.type === 'change_client',
                                                            'pi-calendar-times text-red-500 bg-red-50': template.type === 'cancel_client',
                                                            'pi-bell text-yellow-600 bg-yellow-50': template.type === 'reminder_client',
                                                        }
                                                    ]"
                                                ></span>
                                                <div class="grow">
                                                    <span class=" opacity-90">
                                                        {{ getNotificationLabel(template.type) }}
                                                    </span>
                                                    <CrownIcon :enabled="template.feature_available"
                                                               class="ml-2"></CrownIcon>
                                                </div>

                                            </div>
                                            <div class="flex flex-col grow">
                                                <div
                                                    class="flex flex-col divide-y h-full divide-dashed bg-neutral-50/80 dark:bg-neutral-900 dark:divide-neutral-700 justify-evenly">
                                                    <div class="flex p-3">
                                                        <div
                                                            class="text-sm opacity-90 font-semibold flex items-center gap-1 grow"><span
                                                            class="pi pi-mobile !text-sm"></span><span
                                                            class="mr-2">SMS</span><span
                                                            class="text-xs whitespace-nowrap font-normal text-roori-blue">1 kredit</span>
                                                        </div>
                                                        <ToggleSwitch v-model="template.state_sms"
                                                                      :allow-empty="false"
                                                                      :disabled="!template.feature_available"
                                                                      :options="[{ value: true, icon: 'pi pi-send'}, { value: false, icon: 'pi pi-times'}]"
                                                                      option-value="value" option-label="icon">
                                                            <template #option="{option}">
                                                                <i :class="option.icon"></i>
                                                            </template>
                                                        </ToggleSwitch>
                                                    </div>
                                                    <div class="flex p-3">
                                                        <div
                                                            class="text-sm  opacity-90 font-semibold flex items-center gap-1 grow">
                                                            <span class="pi pi-envelope !text-sm"></span>Email
                                                        </div>
                                                        <ToggleSwitch v-model="template.state"
                                                                      :allow-empty="false"
                                                                      :disabled="!template.feature_available"
                                                                      :options="[{ value: true, icon: 'pi pi-send'}, { value: false, icon: 'pi pi-times'}]"
                                                                      option-value="value" option-label="icon">
                                                            <template #option="{option}">
                                                                <i :class="option.icon"></i>
                                                            </template>
                                                        </ToggleSwitch>
                                                    </div>

                                                    <div v-if="template.type === 'reminder_client'"
                                                         class="!border flex justify-between items-center pl-4 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800 p-2 m-2 mt-0 rounded">
                                                        <p class="text-sm">{{ $t('forms.reminder_settings_label') }}</p>
                                                        <Select :options="reminderOptions"
                                                                v-model="template.options.send_at"
                                                                option-value="id" option-label="label"
                                                                class="w-56"></Select>
                                                    </div>
                                                </div>
                                                <div class="grow"></div>
                                                <Button
                                                    icon="pi pi-pencil"
                                                    severity="secondary"
                                                    class="m-2 mt-0 !border-0"
                                                    size="small"
                                                    :label="$t('forms.template_edit_btn')"
                                                    :disabled="!template.feature_available"
                                                    @click="router.visit(`/app/notifications/${template.id}`)"
                                                />
                                            </div>

                                        </div>
                                    </template>
                                </div>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel value="2">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-sliders-h text-roori-blue"></i>
                                        {{ $t('forms.panel_occupancy') }}
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_occupancy_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <div
                                    class="settings-wrapper p-4 rounded-lg shadow-md border bg-white dark:bg-gray-900 dark:border-neutral-700">
                                    <occupancy-settings v-model="form"></occupancy-settings>
                                </div>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel value="3" class="md:!hidden">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-clock text-roori-blue"></i>
                                        {{ $t('forms.panel_availability') }}
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">
                                        {{ isMultiDayRental ? $t('rental.booking_rules_desc') : $t('forms.drawer_availability_desc') }}
                                    </p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <template v-if="isMultiDayRental">
                                    <div class="flex flex-col gap-3 p-2">
                                        <div class="flex items-center justify-between">
                                            <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('rental.booking_rules_all_objects') }}</p>
                                            <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined
                                                    @click="formBookingRules.push({ day: '', blocking_type: '' })"/>
                                        </div>
                                        <div v-if="formBookingRules.length" class="flex flex-col gap-2">
                                            <div v-for="(rule, i) in formBookingRules" :key="i"
                                                 class="flex gap-2 flex-wrap items-end p-3 rounded-xl border dark:border-neutral-700">
                                                <Select v-model="rule.day" :options="bookingRulesDays"
                                                        option-label="label" option-value="value"
                                                        :placeholder="$t('rental.booking_rule_day_placeholder')" class="grow"/>
                                                <Select v-model="rule.blocking_type" :options="bookingRulesTypes"
                                                        option-label="label" option-value="value"
                                                        :placeholder="$t('rental.booking_rule_type_placeholder')" class="grow"/>
                                                <Button icon="pi pi-trash" text severity="danger" size="small"
                                                        @click="formBookingRules.splice(i, 1)"/>
                                            </div>
                                        </div>
                                        <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.booking_rules_empty') }}</p>
                                    </div>
                                </template>
                                <template v-else>
                                    <edit-time-slot-component v-model="timeSlots" editable
                                                              v-model:deleted="deleted"
                                                              v-model:selected-worker="selectedWorker"
                                                              :worker="props.services.filter(el => el.workers.length > 0).length > 0"
                                                              :workers="_.uniqBy(props.services.map(el => el.workers).flat().map(el => {
                                                                return { name: el.name, id: el.id, initials: el.initials }
                                                              }), 'id')"></edit-time-slot-component>
                                </template>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel v-if="props.form.form_type?.name === 'service'" value="6">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-briefcase text-roori-blue"></i>
                                        {{ $t('forms.panel_services') }}
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_services_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <services-edit v-model="services" :workers="props.workers" :form="props.form"></services-edit>
                            </AccordionContent>
                        </AccordionPanel>
                        <AccordionPanel value="5">
                            <AccordionHeader>
                                <div class="flex flex-col">
                                    <div class="flex gap-5 items-center ml-2">
                                        <i class="pi pi-inbox text-roori-blue"></i>
                                        {{ $t('forms.panel_form_builder') }}
                                        <CrownIcon name="roori_custom_form" class="-ml-3"></CrownIcon>
                                    </div>
                                    <p class="font-normal text-sm ml-11 opacity-70">{{ $t('forms.panel_form_builder_desc') }}</p>
                                </div>
                            </AccordionHeader>
                            <AccordionContent>
                                <FeatureBlocker name="roori_custom_form" version="Pro">
                                    <form-builder v-model="builder"></form-builder>
                                </FeatureBlocker>
                            </AccordionContent>
                        </AccordionPanel>
                    </Accordion>
                </div>

                <div class="p-4 flex justify-between gap-2 border-t dark:border-neutral-700">
                    <Button size="small" @click="showDrawer = (showDrawer === 'theming' ? false : 'theming')"
                            severity="secondary" class="md:!hidden">
                        <template #default>
                            <i :class="!showDrawer ? 'pi pi-palette' : 'pi pi-times'" class="!text-sm"></i>
                            {{ $t('forms.drawer_theming_title') }}
                            <CrownIcon name="roori_can_skin_form"></CrownIcon>
                        </template>
                    </Button>
                    <div class="grow"></div>
                    <Button size="small" severity="secondary" @click="send(true)" :label="$t('forms.save_and_close')"></Button>
                    <Button size="small" @click="send(false)" :label="$t('common.save')"></Button>
                </div>
            </div>
        </template>
    </Drawer>
    <Dialog :visible="!!showDrawer" class="secondary-drawer overflow-hidden" :show-header="false" position="left"
            :show-close-icon="false">
        <template v-if="mounted" #container>
            <Transition name="scale">
                <div :key="showDrawer" class="relative w-full">
                    <div v-if="showDrawer === 'theming'" class="absolute top-0 left-0 right-0 bottom-0">
                        <FeatureBlocker name="roori_theming" unrounded version="Pro">
                            <template #default>
                                <form-theming-interactive v-model:theme="theme" v-model:settings="settings"
                                                          :customForm="builder" thin id="form-wrapper"
                                                          :forms="form">
                                    <template #button>
                                        <Button icon="pi pi-question-circle" severity="contrast" rounded
                                                @click="openPersonalizeDriver"></Button>
                                        <Button icon="pi pi-times" rounded severity="secondary"
                                                @click="showDrawer = false"></Button>
                                    </template>
                                </form-theming-interactive>
                            </template>
                            <template #button>
                                <Button icon="pi pi-times" size="small" class="mb-0.5" fluid :label="$t('common.cancel')" @click="showDrawer = false"
                                        severity="secondary"/>
                            </template>
                        </FeatureBlocker>
                    </div>
                    <div v-if="showDrawer === 'rentalItems'"
                         class="flex flex-col p-8 pt-24 min-h-screen items-center h-screen overflow-y-scroll relative bg-neutral-50 dark:bg-neutral-800">

                        <div
                            class="fixed top-0 left-0 right-0 flex justify-between border-b bg-white/60 dark:border-neutral-700 dark:bg-black/60 backdrop-blur  p-4 pl-8">
                            <div class="flex items-center gap-3">
                                <i class="pi pi-box !text-2xl text-roori-blue"></i>
                                <p class="text-lg font-bold">{{ $t('forms.drawer_rental_items_title') }}</p>
                            </div>
                            <Button icon="pi pi-times" rounded severity="secondary"
                                    @click="showDrawer = false"></Button>
                        </div>
                        <TransitionGroup v-if="rentalItems.rentalItems.length" name="fade-slide" tag="div"
                                         class="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-8">
                            <rental-card
                                v-for="(card, i) in rentalItems.rentalItems"
                                :key="card.index || card.name"
                                :item="card"
                                @remove="() => rentalItems.rentalItems.splice(i, 1)"
                                @edit="() => {
                                      const copy = JSON.parse(JSON.stringify(card));
                                      copy.index = i + 1;
                                      currentItem = copy;
                                    }"></rental-card>

                        </TransitionGroup>
                        <rental-item-modal v-model="currentItem" v-model:form="rentalItems" @save="() => {
                                const snapshot = JSON.parse(JSON.stringify(currentItem));
                                if (snapshot.index)
                                {
                                    rentalItems.rentalItems[snapshot.index - 1] = snapshot;
                                } else {
                                    rentalItems.rentalItems.push(snapshot);
                                }
                                currentItem = false
                            }"></rental-item-modal>

                        <pre v-if="false">{{ rentalItems }}</pre>

                        <div class="grow"></div>

                        <div key="add-more" class="text-center max-w-[600px] my-12 pb-12">
                            <p class="font-bold pb-4">{{ $t('rental.add_more_items') }}</p>
                            <types-cloud v-model="selectedType" class="justify-center"></types-cloud>
                        </div>

                    </div>
                    <div v-if="showDrawer === 'availability'"
                         class="fixed top-0 left-0 right-0 bottom-0 bg-neutral-50 dark:bg-black h-full">
                        <div class=" max-md:hidden overflow-hidden dark:border-neutral-700 transition-all"
                             id="availability-wrapper" :class="{'ring-2 ring-offset-2':selectedPlan !== 'default'}">

                            <plan-calendar v-model="timeSlots" v-model:plan="selectedPlan"
                                           v-model:selected-worker="selectedWorker" v-model:deleted="deleted"
                                           :show-workers="!!services.filter(el => el.workers.length).length"
                                           :workers="uniqBy(services.map(el => el.workers.map(w => { return { name: w.name, initials: w.initials, id: w.id, genitive: w.genitive } })).flat(), 'id')">
                                <template #buttons>
                                    <Button icon="pi pi-question-circle" severity="contrast" rounded
                                            @click="openAvailabilityDriver"></Button>
                                    <Button icon="pi pi-times" rounded severity="secondary"
                                            @click="showDrawer = false"></Button>
                                </template>
                            </plan-calendar>
                            <transition :name="animationName" mode="out-in">
                                <div
                                    :key="computedSelected"
                                    :class="timeSlots[computedSelected]?.type === 'vacation' ? 'blur' : ''"
                                >
                                    <calendar-based-time-slot
                                        @change-view="doComputeSlotHeight"
                                        :slot-height="slotHeight"
                                        v-model:form="form"
                                        :form-type="props.form.form_type?.name"
                                        v-model="timeSlots[computedSelected]"
                                        v-model:selected-worker="selectedWorker"
                                    ></calendar-based-time-slot>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </Transition>
        </template>
    </Dialog>
</template>

<style lang="scss">
.main-drawer {
    width: 550px !important;

    @media (max-width: 1500px) {
        width: 480px !important;
    }
    @media (max-width: 768px) {
        width: 100dvw !important;
    }
}

.secondary-drawer {
    background: white;
    height: 100dvh;
    max-height: calc(100% - 20px) !important;
    width: calc(100vw - 570px) !important;
    box-shadow: none !important;
    @media (max-width: 1500px) {
        width: calc(100vw - 500px) !important;
    }
    @media (max-width: 1368px) {
        width: 100dvw !important;
    }
}


.status-col {
    display: flex;
    align-items: center;
    gap: 6px;

    .status-show {
        width: 8px;
        height: 8px;
        border-radius: 99px;
        display: block;
        background: black;

        &.online {
            background: rgb(106, 204, 13);

        }

        &.offline {
            background: rgb(204, 13, 13);

        }
    }
}

.setting-item {
    display: flex;
    flex-direction: column;
}

.dark .ql-toolbar.ql-snow {
    border: 1px solid #444;
}

.dark .ql-container {
    border-color: #444 !important;
}

.dark .ql-snow .ql-stroke {
    stroke: #999;
}

</style>
